import React, { useState, useEffect } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Divider
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import axios from 'axios'
import { ApiConfig } from '../../config/api'
import { useApi } from '../../hooks/useApi'
import { SettingsList } from './SettingsList'
import { getSettingIcon } from './settingIcons'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../SnackBar/Snackbar'
import { Status } from '../../hooks/useFetchState'
import { Loading } from '../UI'
import { SettingsData } from '../../interfaces/Settings/settings'

interface Section {
  label: string
  metadataIds: number[]
}

interface TypesSettingProps {
  typeSettingId: number
  name: string
  description: string
  brandId: number | null
  forcedDisabledIds?: Set<number>
  conflictingMetaIds?: Set<number>
  pendingValuesByValuesId?: Record<number, string>
  sections?: Section[]
  hiddenMetadataIds?: number[]
  onSettingChange?: (
    settingsValuesId: number,
    newValue: string,
    productOriginId: number | null,
    isDirty: boolean
  ) => void
  onCollapse?: () => void
  onSettingsLoaded?: (settings: SettingsData[]) => void
  hasPendingChanges?: boolean
}

const getSettingsValues = (typeSettingId: number, brandId: number | null) => {
  const url = new URL(`${ApiConfig.SettiingsApi}/typeSetting/${typeSettingId}`)
  if (brandId !== null) {
    url.searchParams.set('productOriginId', String(brandId))
  }
  return axios.get(url.toString())
}

export const TypesSetting = ({
  typeSettingId,
  name,
  description,
  brandId,
  forcedDisabledIds,
  conflictingMetaIds,
  pendingValuesByValuesId,
  sections,
  hiddenMetadataIds,
  onSettingChange,
  onCollapse,
  onSettingsLoaded,
  hasPendingChanges = false
}: TypesSettingProps) => {
  const { callApi, state } = useApi()
  const [detail, setDetail] = useState<SettingsData[]>([])
  const [expanded, setExpanded] = useState(true)
  const [loadedBrandId, setLoadedBrandId] = useState<number | null | undefined>(
    undefined
  )
  const { isActive, message, isSuccess, openSnackBar } = useSnackbar()

  useEffect(() => {
    // Solo carga la primera vez por brandId; no recarga al contraer/expandir
    if (!expanded || loadedBrandId === brandId) return

    callApi(getSettingsValues(typeSettingId, brandId))
      .then((response) => {
        const data = response?.data ?? response
        const loaded = Array.isArray(data) ? data : []
        setDetail(loaded)
        setLoadedBrandId(brandId)
        onSettingsLoaded?.(loaded)
      })
      .catch((err) =>
        openSnackBar(
          `Error al cargar los parámetros de configuración [${err}]`,
          false
        )
      )
  }, [expanded, brandId])

  const SettingIcon = getSettingIcon(typeSettingId)

  const remainderSettings = sections
    ? detail.filter(
        (s) =>
          !sections.some((section) =>
            section.metadataIds.includes(s.settingsMetadataId)
          ) && !hiddenMetadataIds?.includes(s.settingsMetadataId)
      )
    : []

  return (
    <>
      <Accordion
        disableGutters
        defaultExpanded
        onChange={(_, isExpanded) => {
          setExpanded(isExpanded)
          if (!isExpanded) {
            onCollapse?.()
          }
        }}
        sx={{
          mb: 1,
          borderRadius: '12px !important',
          overflow: 'hidden',
          boxShadow: hasPendingChanges
            ? '0px 0px 0px 2px #ed6c02'
            : '0px 1px 4px rgba(0,0,0,0.08)',
          '&:before': { display: 'none' }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 3,
            py: 1.5,
            bgcolor: hasPendingChanges ? '#fff3e0' : 'transparent',
            borderRadius: '12px'
          }}
        >
          <Box display='flex' alignItems='center' gap={2} width='100%'>
            <Box
              sx={{
                bgcolor: hasPendingChanges ? 'warning.light' : 'primary.50',
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <SettingIcon
                fontSize='small'
                color={hasPendingChanges ? 'warning' : 'primary'}
              />
            </Box>
            <Box flex={1}>
              <Typography variant='body1' fontWeight={500}>
                {name}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {description}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          {state.status === Status.Loading && (
            <Typography>
              <Loading />
            </Typography>
          )}
          {state.status === Status.Success && detail.length > 0 ? (
            sections ? (
              <>
                {remainderSettings.length > 0 && (
                  <SettingsList
                    settings={remainderSettings}
                    forcedDisabledIds={forcedDisabledIds}
                    conflictingMetaIds={conflictingMetaIds}
                    pendingValuesByValuesId={pendingValuesByValuesId}
                    onSettingChange={
                      onSettingChange
                        ? (valuesId, value, originId, dirty) =>
                            onSettingChange(valuesId, value, originId, dirty)
                        : undefined
                    }
                  />
                )}
                {sections.map((section, idx) => {
                  const sectionSettings = detail.filter((s) =>
                    section.metadataIds.includes(s.settingsMetadataId)
                  )
                  if (sectionSettings.length === 0) return null
                  const showDivider = idx > 0 || remainderSettings.length > 0
                  return (
                    <Box key={section.label} sx={{ mt: showDivider ? 1.5 : 0 }}>
                      {showDivider && <Divider />}
                      <Box
                        pl={2}
                        pr={3}
                        py={1.25}
                        sx={{
                          borderLeft: '3px solid',
                          borderColor: 'primary.main',
                          bgcolor: 'primary.50',
                          borderBottom: '1px solid',
                          borderBottomColor: 'divider'
                        }}
                      >
                        <Typography
                          variant='body2'
                          fontWeight={600}
                          color='primary.main'
                        >
                          {section.label}
                        </Typography>
                      </Box>
                      <SettingsList
                        settings={sectionSettings}
                        forcedDisabledIds={forcedDisabledIds}
                        conflictingMetaIds={conflictingMetaIds}
                        pendingValuesByValuesId={pendingValuesByValuesId}
                        onSettingChange={
                          onSettingChange
                            ? (valuesId, value, originId, dirty) =>
                                onSettingChange(
                                  valuesId,
                                  value,
                                  originId,
                                  dirty
                                )
                            : undefined
                        }
                      />
                    </Box>
                  )
                })}
              </>
            ) : (
              <SettingsList
                settings={detail}
                forcedDisabledIds={forcedDisabledIds}
                pendingValuesByValuesId={pendingValuesByValuesId}
                onSettingChange={
                  onSettingChange
                    ? (valuesId, value, originId, dirty) =>
                        onSettingChange(valuesId, value, originId, dirty)
                    : undefined
                }
              />
            )
          ) : (
            state.status === Status.Success && (
              <Typography
                textAlign='center'
                variant='body2'
                color='text.secondary'
                py={2}
              >
                Sin parámetros de configuración
              </Typography>
            )
          )}
        </AccordionDetails>
      </Accordion>

      <SnackBar
        open={isActive}
        message={message}
        status={isSuccess ? 'success' : 'error'}
      />
    </>
  )
}
